import React, { useState, useRef, useCallback } from 'react';
import { adminUpload } from '../api';

const SUPABASE_URL    = process.env.REACT_APP_SUPABASE_URL || '';
const STORAGE_BASE    = `${SUPABASE_URL}/storage/v1/object/public/thumbnails`;

/** Mirror the toImageUrl logic from src/lib/menuData.js */
function toImageUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  const clean = imagePath.startsWith('/images/') ? imagePath.slice('/images/'.length) : imagePath;
  return `${STORAGE_BASE}/${clean}`;
}

/**
 * ImageInput — tabbed control for choosing an image.
 *
 * Props:
 *   value     {string}           — current image_path value
 *   onChange  {(path: string) => void}
 *   label     {string}
 */
export default function ImageInput({ value, onChange, label = 'Image' }) {
  const [tab, setTab]         = useState('upload');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]     = useState(null);
  const [urlInput, setUrlInput] = useState(value || '');
  const fileInputRef            = useRef(null);

  const previewUrl = value ? toImageUrl(value) : null;

  // ── Upload tab ───────────────────────────────────────────────

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const { image_path } = await adminUpload(file);
      onChange(image_path);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  }, [handleFile]);

  // ── URL/Path tab ─────────────────────────────────────────────

  const applyUrl = () => {
    const trimmed = urlInput.trim();
    if (trimmed) onChange(trimmed);
  };

  return (
    <div className="image-input">
      <div className="image-input-label">{label}</div>

      <div className="image-input-tabs">
        <button
          type="button"
          className={`image-tab-btn${tab === 'upload' ? ' active' : ''}`}
          onClick={() => setTab('upload')}
        >
          Upload
        </button>
        <button
          type="button"
          className={`image-tab-btn${tab === 'url' ? ' active' : ''}`}
          onClick={() => setTab('url')}
        >
          URL / Path
        </button>
      </div>

      {tab === 'upload' && (
        <div
          className={`image-dropzone${dragging ? ' drag-over' : ''}${uploading ? ' uploading' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files?.[0])}
          />
          {uploading
            ? <span>Uploading…</span>
            : <span>Drop an image here or click to browse</span>}
        </div>
      )}

      {tab === 'url' && (
        <div className="image-url-row">
          <input
            type="text"
            placeholder="/images/my-image.webp or https://..."
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onBlur={applyUrl}
            onKeyDown={e => e.key === 'Enter' && applyUrl()}
          />
        </div>
      )}

      {error && <div className="admin-form-error">{error}</div>}

      {previewUrl && (
        <div className="image-preview">
          <img src={previewUrl} alt="Preview" />
          <div className="image-preview-path">{value}</div>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { onChange(''); setUrlInput(''); }}>
            Remove
          </button>
        </div>
      )}

      <style>{`
        .image-input { margin-bottom: 4px; }
        .image-input-label { font-size: 12px; font-weight: 600; color: #444; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 6px; }
        .image-input-tabs { display: flex; gap: 2px; margin-bottom: 8px; }
        .image-tab-btn { padding: 5px 12px; border: 1px solid #ccc; border-radius: 4px; background: #f5f5f5; cursor: pointer; font-size: 12px; }
        .image-tab-btn.active { background: #4f6ef5; color: #fff; border-color: #4f6ef5; }
        .image-dropzone {
          border: 2px dashed #ccc; border-radius: 6px; padding: 28px;
          text-align: center; color: #888; font-size: 13px; cursor: pointer;
          transition: border-color .15s, background .15s;
        }
        .image-dropzone:hover, .image-dropzone.drag-over { border-color: #4f6ef5; background: #f0f4ff; }
        .image-dropzone.uploading { opacity: .6; cursor: default; }
        .image-url-row input { width: 100%; box-sizing: border-box; padding: 7px 10px; border: 1px solid #ccc; border-radius: 4px; font-size: 13px; }
        .image-preview { display: flex; align-items: center; gap: 12px; margin-top: 10px; padding: 8px; background: #f9f9f9; border: 1px solid #eee; border-radius: 4px; }
        .image-preview img { width: 52px; height: 52px; object-fit: cover; border-radius: 3px; border: 1px solid #ddd; }
        .image-preview-path { font-size: 11px; color: #888; flex: 1; word-break: break-all; }
      `}</style>
    </div>
  );
}
